import styled, { useTheme } from "styled-components";
import ButtonBase from "@mui/material/ButtonBase";
import { useState } from "react";
import { Box } from "@mui/material";
import { useSelector } from "react-redux";
import { LAPTOP, WIDESCREEN } from "../../../../../constants/widget";

const SmileyContainer = styled(Box)({
  display: "flex",
  justifyContent: "center",
  gap: "10px",
  marginTop: "50px",
});

const SmileyItem = styled(Box)(({ theme }) => ({
  position: "relative",
  width: "80px",
  height: "140px",
  "&:not(:last-child)::after": {
    content: "''",
    position: "absolute",
    top: "clamp(2.5vh, 20%, 5.5vh) !important",
    left: "80%",
    width: "54px",
    height: "2px",
    backgroundColor: theme.primaryColor,
    transform: "translateY(-50%)",
    zIndex: "0 !important",
  },
}));

const FaceWrapper = styled(Box)({
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  display: "flex",
  justifyContent: "center",
});

const LabelWrapper = styled(({ windowDimensions, ...rest }) => <Box {...rest} />)(({ theme, windowDimensions }) => ({
  position: "absolute",
  top: "50%",
  left: 0,
  right: 0,
  textAlign: "center",
  padding: "0 4px",
  fontSize: `${(windowDimensions?.width <= LAPTOP || windowDimensions?.width <= WIDESCREEN ? theme.xs : theme.md)}`,
  lineBreak: "auto"
}));

const SmileyBase = styled(({active,ariaLabel, ...rest}) => <ButtonBase {...rest} role="radio" aria-checked={active} aria-label={ariaLabel}/>)(({ theme, active }) => ({
  width: "55px",
  aspectRatio: "1 / 1",
  border: `2px solid ${theme.primaryColor} !important`,
  borderRadius: "50% !important",
  backgroundColor: `${active ? theme.primaryColor : "#fff"} !important`,
  position: "relative",
  "&:focus-visible": {
    outline: `2px solid ${theme.primaryColor}`,
    outlineOffset: "2px",
  },
}));

const Eye = styled(({ active, ...rest }) => <Box {...rest} />)(({ theme, active }) => ({
  position: "absolute",
  top: "38%",
  width: "6px",
  height: "6px",
  background: active ? "#fff" : theme.primaryColor,
  borderRadius: "50%",
}));

const Eyebrow = styled(({ active, ...rest }) => <Box {...rest} />)(({ theme, active }) => ({
  position: "absolute",
  width: "14px",
  height: "3px",
  background: active ? "#fff" : theme.primaryColor,
  top: "12px",
}));

const Mouth = styled(({ active, ...rest }) => <Box {...rest} />)(({ theme, active }) => ({
  position: "absolute",
  left: "50%",
  transform: "translateX(-50%)",
  bottom: "30%",
  border: `2px solid ${active ? "#fff" : theme.primaryColor}`,
  borderRadius: "50%",
}));

export const HighlyUnsatisfiedSmiley = ({ active, onClick }) => {
  const theme = useTheme();
  return (
    <SmileyBase
      active={active}
      onClick={onClick}
      ariaLabel="Highly Unsatisfied"
      sx={{ zIndex: "2 !important" }}
    >
      <Eyebrow active={active} style={{ left: "15%", transform: "rotate(-140deg)" }} />
      <Eyebrow active={active} style={{ right: "15%", transform: "rotate(140deg)" }} />
      <Eye active={active} style={{ left: "27%" }} />
      <Eye active={active} style={{ right: "27%" }} />
      <Mouth
        sx={{
          borderColor: `${active ? "#fff" : theme.primaryColor} transparent transparent transparent`,
          width: "25px",
          height: "9px",
          bottom: "12%",
        }}
      />
    </SmileyBase>
  );
};

export const UnsatisfiedSmiley = ({ active, onClick }) => {
  const theme = useTheme();
  return (
    <SmileyBase
      active={active}
      onClick={onClick}
      ariaLabel="Unsatisfied"
      sx={{ zIndex: "2 !important" }}
    >
      <Eye active={active} style={{ left: "27%" }} />
      <Eye active={active} style={{ right: "27%" }} />
      <Mouth
        sx={{
          borderColor: `${active ? "#fff" : theme.primaryColor} transparent transparent transparent`,
          width: "25px",
          height: "9px",
          bottom: "12%",
        }}
      />
    </SmileyBase>
  );
};

export const NeutralSmiley = ({ active, onClick }) => {
  const theme = useTheme();
  return (
    <SmileyBase
      active={active}
      onClick={onClick}
      ariaLabel="Neutral"
      sx={{ zIndex: "2 !important" }}
    >
      <Eye active={active} style={{ left: "27%" }} />
      <Eye active={active} style={{ right: "27%" }} />
      <Mouth
        sx={{
          borderRadius: "0",
          borderWidth: "2px 0 0 0",
          borderColor: active ? "#fff" : theme.primaryColor,
          height: "0",
          width: "25px",
          zIndex: 3,
        }}
      />
    </SmileyBase>
  );
};

export const SatisfiedSmiley = ({ active, onClick }) => {
  const theme = useTheme();
  return (
    <SmileyBase
      active={active}
      onClick={onClick}
      ariaLabel="Satisfied"
      sx={{ zIndex: "2 !important" }}
    >
      <Eye active={active} style={{ left: "27%" }} />
      <Eye active={active} style={{ right: "27%" }} />
      <Mouth
        sx={{
          borderColor: `transparent transparent ${active ? "#fff" : theme.primaryColor} transparent`,
          width: "25px",
          height: "12px",
        }}
      />
    </SmileyBase>
  );
};

export const HighlySatisfiedSmiley = ({ active, onClick }) => {
  const theme = useTheme();
  return (
    <SmileyBase
      active={active}
      onClick={onClick}
      ariaLabel="Highly Satisfied"
      sx={{ zIndex: "2 !important" }}
    >
      <Eye active={active} style={{ left: "27%" }} />
      <Eye active={active} style={{ right: "27%" }} />
      <Mouth
        sx={{
          borderColor: `transparent ${active ? "#fff" : theme.primaryColor} ${active ? "#fff" : theme.primaryColor} ${active ? "#fff" : theme.primaryColor}`,
          width: "25px",
          height: "9px",
          bottom: "20%",
          "&::before": {
            content: '""',
            position: "absolute",
            width: "25px",
            border: `1px solid ${active ? "#fff" : theme.primaryColor}`,
            backgroundColor: active ? "#fff" : theme.primaryColor,
            bottom: "75%",
            left: "50%",
            transform: "translateX(-50%)",
          },
        }}
      />
    </SmileyBase>
  );
};

export default function SmileyRatings({ data, formState, onChange, ariaLabelledby }) {
  const windowDimensions = useSelector((state) => state?.content?.windowDimensions);

  return (
    <SmileyContainer role="radiogroup" aria-labelledby={ariaLabelledby}>
      <SmileyItem>
        <FaceWrapper>
          <HighlyUnsatisfiedSmiley
            active={formState?.[data?.field] === "HIGHLY_UNSATISFIED"}
            onClick={() => onChange("HIGHLY_UNSATISFIED", data?.field)}
          />
        </FaceWrapper>
        <LabelWrapper windowDimensions={windowDimensions}>Highly Unsatisfied</LabelWrapper>
      </SmileyItem>
      <SmileyItem>
        <FaceWrapper>
          <UnsatisfiedSmiley
            active={formState?.[data?.field] === "UNSATISFIED"}
            onClick={() => onChange("UNSATISFIED", data?.field)}
          />
        </FaceWrapper>
        <LabelWrapper windowDimensions={windowDimensions}>Unsatisfied</LabelWrapper>
      </SmileyItem>
      <SmileyItem>
        <FaceWrapper>
          <NeutralSmiley
            active={formState?.[data?.field] === "NEUTRAL"}
            onClick={() => onChange("NEUTRAL", data?.field)}
          />
        </FaceWrapper>
        <LabelWrapper windowDimensions={windowDimensions}>Neutral</LabelWrapper>
      </SmileyItem>
      <SmileyItem>
        <FaceWrapper>
          <SatisfiedSmiley
            active={formState?.[data?.field] === "SATISFIED"}
            onClick={() => onChange("SATISFIED", data?.field)}
          />
        </FaceWrapper>
        <LabelWrapper windowDimensions={windowDimensions}>Satisfied</LabelWrapper>
      </SmileyItem>
      <SmileyItem>
        <FaceWrapper>
          <HighlySatisfiedSmiley
            active={formState?.[data?.field] === "HIGHLY_SATISFIED"}
            onClick={() => onChange("HIGHLY_SATISFIED", data?.field)}
          />
        </FaceWrapper>
        <LabelWrapper windowDimensions={windowDimensions}>Highly Satisfied</LabelWrapper>
      </SmileyItem>
    </SmileyContainer>
  );
}
